import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import TextField from "@mui/material/TextField";
import PropTypes from "prop-types";
import Portfolio from "@meetyoo/components/src/components/Portfolio";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LinearProgress from "@mui/material/LinearProgress";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ErrorModal from "@meetyoo/components/src/components/Modal/ErrorModal";
import {
  createBill,
  setErrorMessage,
  resetBillingError,
  resetContract,
  setSettleContractDate,
  setReserveContractDate,
  resetGetBillError,
} from "../../../actions/billing";
import { moment } from "../../../utils/LocalizedUtils";

import { useDefaultPasscodeStyles } from "../../../constants/passCodeUtils";

export const ownMoment = moment();

export const BillingEventComponent = ({
  billingEventContract,
  createBillHandler,
  errorMessage,
  setErrorMessageHandler,
  resetBillingErrorHandler,
  getBillErrorResponse,
  requestBillingInProgress,
  resetContractHandler,
  requestResetContract,
  reserveContractDate,
  settleContractDate,
  setSettleContractDateFn,
  setReserveContractDateFn,
  resetGetBillErrorFn,
}) => {
  const theme = useTheme();
  const pc = useDefaultPasscodeStyles();
  const sx = useMemo(
    () => ({
      innercontainer: { display: "inline-flex" },
      container: { textAlign: "center", display: "inline" },
      button: { m: theme.spacing(3, 0, 2, 2) },
      binding: { p: theme.spacing(4, 1, 1, 1) },
      buttonSpace: { width: "200px" },
      linebreak: { width: "100%" },
      invalidInput: {
        "& input:invalid": {
          background: "#F08080",
        },
      },
    }),
    [theme]
  );
  const [openErrorModal, setOpenErrorModal] = useState(false);

  const [contractNoFrom, setContractNoFrom] = useState(0);
  const [contractNoTo, setContractNoTo] = useState(0);
  const [contractNoCount, setContractNoCount] = useState(0);
  const [makeRequest, setMakeRequest] = useState(false);
  const [postingDate, setPostingDate] = useState(settleContractDate || moment(ownMoment).tz("UTC"));
  const [resetContractMonth, setResetContractMonth] = useState(
    reserveContractDate ||
      moment(ownMoment)
        .startOf("month")
        .tz("UTC")
  );
  const [resetContractNo, setResetContractNo] = useState(0);

  useEffect(() => {
    setOpenErrorModal(errorMessage !== "");
    const request = async () => {
      if (makeRequest && contractNoCount <= contractNoTo && !requestBillingInProgress && !getBillErrorResponse) {
        setContractNoCount(contractNoCount + 1);
        await createBillHandler(contractNoCount, postingDate.format("DD.MM.YYYY"));
        if (contractNoCount + 1 > contractNoTo) setMakeRequest(false);
      }
    };
    if (getBillErrorResponse) setMakeRequest(false);
    request();
  }, [
    openErrorModal,
    errorMessage,
    makeRequest,
    setMakeRequest,
    contractNoFrom,
    contractNoTo,
    getBillErrorResponse,
    requestBillingInProgress,
    contractNoCount,
    createBillHandler,
    postingDate,
  ]);

  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
    setErrorMessageHandler("");
  };

  return [
    <Portfolio
      title="Ledger Entries zurücksetzen"
      key="container-contract_reset"
      component={
        <Box sx={sx.container}>
          <div>
            <Box sx={sx.innercontainer}>
              <TextField
                id="contract-reset-input"
                label="Contract Number"
                value={resetContractNo}
                onChange={event => setResetContractNo(event.target.value)}
                margin="normal"
                sx={[pc.textField, sx.invalidInput]}
                slotProps={{ htmlInput: { pattern: "[0-9]*" } }}
              />
              <DatePicker
                format="DD.MM.YYYY"
                value={resetContractMonth ? moment(resetContractMonth) : null}
                onChange={date => {
                  setResetContractMonth(date);
                  setReserveContractDateFn(date);
                }}
                slotProps={{
                  textField: {
                    id: "ledger-entry-starting-time",
                    label: "Beginning of the month",
                    sx: [pc.textField, pc.lineExpander],
                    margin: "normal",
                  },
                }}
              />
            </Box>
            <Box sx={pc.lineBreak} />
            <Box sx={sx.innercontainer}>
              <Box sx={sx.buttonSpace} />
              <Button
                disabled={requestResetContract}
                variant="contained"
                sx={sx.button}
                onClick={() => {
                  resetContractHandler(resetContractNo, resetContractMonth.format("DD.MM.YYYY"));
                }}
              >
                Revert
              </Button>
            </Box>
          </div>
        </Box>
      }
    />,
    <Portfolio
      title="Verträge abrechnen"
      key="container-contract"
      component={
        <Box sx={sx.container}>
          <div>
            <Box sx={sx.innercontainer}>
              <TextField
                id="contract-input"
                label="Contract Number From"
                value={contractNoFrom.toString()}
                onChange={event => setContractNoFrom(parseInt(event.target.value, 10) || event.target.value)}
                margin="normal"
                sx={[pc.textField, sx.invalidInput]}
                slotProps={{ htmlInput: { pattern: "[0-9]*" } }}
              />
              <Box sx={sx.binding}>-</Box>
              <TextField
                id="contract-input"
                label="Contract Number To"
                value={contractNoTo.toString()}
                onChange={event => setContractNoTo(parseInt(event.target.value, 10) || event.target.value)}
                margin="normal"
                sx={[pc.textField, sx.invalidInput]}
                slotProps={{ htmlInput: { pattern: "[0-9]*" } }}
              />
            </Box>
            <Box sx={pc.lineBreak} />
            <DatePicker
              format="DD.MM.YYYY"
              value={postingDate || null}
              onChange={date => {
                setPostingDate(date);
                setSettleContractDateFn(date);
              }}
              slotProps={{
                textField: {
                  id: "ledger-entry-starting-time-bill",
                  label: "Date",
                  sx: [pc.textField, pc.lineExpander],
                  margin: "normal",
                },
              }}
            />
            <Button
              variant="contained"
              sx={sx.button}
              onClick={() => {
                setContractNoCount(contractNoFrom);
                setMakeRequest(true);
                resetGetBillErrorFn();
                resetBillingErrorHandler();
              }}
            >
              Create Bill
            </Button>
            <Box sx={sx.linebreak} />

            {contractNoCount - 1 <= contractNoTo && makeRequest && (
              <div>
                <div>Doing Document Number: {contractNoCount - 1}</div>
                <LinearProgress
                  variant="determinate"
                  value={((contractNoCount - contractNoFrom) / (contractNoTo - contractNoFrom)) * 100}
                />
                <Button
                  variant="contained"
                  sx={sx.button}
                  onClick={() => {
                    setMakeRequest(false);
                  }}
                >
                  STOP!
                </Button>
              </div>
            )}
            {getBillErrorResponse && <div>Problem on Document Number: {contractNoCount - 1}</div>}
            {contractNoCount > contractNoTo && !makeRequest && <div>All Jobs Done!</div>}
            {billingEventContract !== "" && <div>Document Number: {billingEventContract}</div>}
          </div>
        </Box>
      }
    />,

    <ErrorModal
      key="error"
      handleCloseErrorModal={handleCloseErrorModal}
      openErrorModal={openErrorModal}
      errorMessage={errorMessage}
    />,
  ];
};

BillingEventComponent.defaultProps = {
  errorMessage: "",
  reserveContractDate: undefined,
  settleContractDate: undefined,
};

BillingEventComponent.propTypes = {
  billingEventContract: PropTypes.string.isRequired,
  createBillHandler: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  setErrorMessageHandler: PropTypes.func.isRequired,
  getBillErrorResponse: PropTypes.bool.isRequired,
  requestBillingInProgress: PropTypes.bool.isRequired,
  resetBillingErrorHandler: PropTypes.func.isRequired,
  // translate: PropTypes.object.isRequired,
  reserveContractDate: PropTypes.object,
  settleContractDate: PropTypes.object,
  setReserveContractDateFn: PropTypes.func.isRequired,
  setSettleContractDateFn: PropTypes.func.isRequired,
  resetGetBillErrorFn: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  billingEventContract: state.billing.billingEventContract,
  errorMessage: state.billing.errorMessage,
  getBillErrorResponse: state.billing.getBillErrorResponse,
  requestBillingInProgress: state.billing.requestBillingInProgress,
  requestResetContract: state.billing.requestResetContract,
  reserveContractDate: state.billing.reserveContractDate,
  settleContractDate: state.billing.settleContractDate,
});

const mapDispatchToProps = dispatch => ({
  setErrorMessageHandler: errorMessage => dispatch(setErrorMessage(errorMessage)),
  createBillHandler: async (contractNumber, postingDate) => {
    await dispatch(createBill(contractNumber, postingDate));
  },
  resetBillingErrorHandler: () => dispatch(resetBillingError()),
  resetContractHandler: (contractNo, postingDate) => dispatch(resetContract(contractNo, postingDate)),
  setReserveContractDateFn: date => dispatch(setReserveContractDate(date)),
  setSettleContractDateFn: date => dispatch(setSettleContractDate(date)),
  resetGetBillErrorFn: () => dispatch(resetGetBillError()),
});

const BillingEvent = connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingEventComponent);

export default BillingEvent;
